import type {
  APIShareRegionalTrendItem,
  APISharedProfileResponse,
} from '@see-stella/types'
import { sDayJs } from '@see-stella/utils'
import { useEffect, useMemo, useRef, useState } from 'react'
import { AbstractFace } from '../../components/AbstractFace'
import {
  buildMetricChart,
  buildRegionalChart,
  dayIndexOfScan,
  type SeriesItem,
} from './chart'
import { labelForArea } from './labels'
import type { LifestyleSignal, LifestyleSignalId } from './lifestyle'
import { buildLifestyleOverlay } from './lifestyleOverlay'
import {
  displayScore,
  metricColorVar,
  photoForScan,
  projectCoverPoint,
  seriesFor,
  valueAt,
} from './model'
import { useImagePrefetch } from './useImagePrefetch'
import type { ReportActions, ReportState } from './useReportState'
import type { SectionVisibility } from './visibility'
import './ScanPlayer.css'

interface ScanPlayerProps {
  profile: APISharedProfileResponse
  state: ReportState
  actions: ReportActions
  /**
   * Per-scan region dots for the current metric, indexed by scanId (from the
   * lazy regional-trend fetch). Lets dots render on EVERY scan, not just the
   * latest one getProfile carries. Empty until the trend loads.
   */
  regionalDots?: Map<string, APIShareRegionalTrendItem>
  /**
   * Which sub-components are visible, computed once by the parent (from the
   * header for the recipient, or the owner control bar). Passed in so the owner's
   * live toggles and the recipient share the exact same gating.
   */
  vis: SectionVisibility
  /**
   * Lifestyle (wearable) overlay: whether it's shared and which signals actually
   * carry data. When shared, the chart gains an overlay picker that draws one
   * signal as a second, right-axis-scaled line over the same dates. Absent = the
   * feature is off entirely (no picker, chart unchanged).
   */
  lifestyle?: { shared: boolean; signals: LifestyleSignal[] }
}

// photo frame aspect (width / height) — must match .player__frame in the CSS.
const FRAME_ASPECT = 4 / 5
// chart viewBox geometry. VBH is tall (close to the rendered aspect) so the
// preserveAspectRatio=none stretch barely distorts the axis text. A proper
// responsive chart lib would remove the stretch entirely, but this reads fine.
const VBW = 820
const VBH = 560
const PL = 44
const PR = 16
const PT = 20
const PB = 30

type LineKey = 'trend' | 'daily' | 'baseline'
type Zoom = 'all' | '3m' | '1m'
const ZOOM_DAYS: Record<Zoom, number> = { all: Infinity, '3m': 90, '1m': 30 }

/**
 * The unified scan player: the photo and the metric chart are ONE component
 * driven by a single date cursor (drag, arrow keys, or the play/pause
 * time-lapse). The photo frame is a FIXED size across every scan and images are
 * window-prefetched so playback swaps instantly. The chart mimics Deep Dive:
 * Trend (ema), Daily (emaFast), Baseline (baselineEma) over a daily calendar
 * domain, with routine markers and a zoom to read a full timeline. One metric.
 */
export function ScanPlayer({
  profile,
  state,
  actions,
  regionalDots,
  vis,
  lifestyle,
}: ScanPlayerProps) {
  const { scans, scanIdx, metric, playing } = state
  const scan = scans[scanIdx]
  // Region drill-down: tapping a dot selects that area; the chart then shows
  // that region's trend over time. null = the overall face-level metric.
  const [selectedArea, setSelectedArea] = useState<string | null>(null)
  // A region belongs to the current metric — switching metrics returns to face.
  useEffect(() => {
    setSelectedArea(null)
  }, [metric])
  const regionalItems = useMemo(
    () => (regionalDots ? [...regionalDots.values()] : []),
    [regionalDots]
  )
  // Drilling into a region needs the loaded trend to build the chart from. Before
  // the trend arrives (or if its fetch failed) the fallback dots still render on
  // the latest scan, but they must NOT be tappable — otherwise a tap would build
  // an empty regional chart and strand the viewer on a blank graph.
  const canDrill = regionalItems.length > 0
  const chart = useMemo(
    () =>
      selectedArea && regionalItems.length > 0
        ? buildRegionalChart(profile, regionalItems, selectedArea)
        : buildMetricChart(profile, metric),
    [profile, metric, selectedArea, regionalItems]
  )
  const color = metricColorVar(metric)

  const [show, setShow] = useState<Record<LineKey, boolean>>({
    trend: true,
    daily: true,
    baseline: false,
  })
  const toggle = (k: LineKey) => setShow((s) => ({ ...s, [k]: !s[k] }))
  const [zoom, setZoom] = useState<Zoom>('all')

  // ----- lifestyle overlay selection -----
  // Only signals that actually carry data are offered. `overlaySig` is the user's
  // choice; `overlayEff` derives what actually draws: never a signal when
  // lifestyle isn't shared, and — with metrics hidden (no skin line to sit under)
  // — the first signal by default so the chart is never an empty pair of axes.
  const lifeSignals = lifestyle?.signals ?? []
  const lifeShared = (lifestyle?.shared ?? false) && lifeSignals.length > 0
  const [overlaySig, setOverlaySig] = useState<'off' | LifestyleSignalId>('off')
  const overlayEff: 'off' | LifestyleSignalId = !lifeShared
    ? 'off'
    : overlaySig !== 'off'
      ? overlaySig
      : vis.metrics
        ? 'off'
        : lifeSignals[0].id
  const overlayActive = overlayEff !== 'off'
  // Right padding widens when the overlay is on to make room for its axis labels.
  const prEff = overlayActive ? 78 : PR
  // expanded = photo-primary layout: big full-width photo on top, then the
  // scrubber, then the chart underneath (vs. the default side-by-side).
  const [expanded, setExpanded] = useState(false)
  // Region dots overlay the face by default; the viewer can hide them to read
  // the photo (and the face-level score) unobstructed.
  const [showRegions, setShowRegions] = useState(true)

  // ----- window image prefetch (±10 around the cursor, evict beyond) -----
  const imageUrls = useMemo(
    () => scans.map((s) => photoForScan(profile, s.scanId)?.imageUrl),
    [profile, scans]
  )
  useImagePrefetch(vis.photos ? imageUrls : [], scanIdx)

  // ----- time-lapse -----
  const reduceMotion =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  useEffect(() => {
    if (!playing) return
    if (reduceMotion) {
      actions.setScanIdx(scans.length - 1)
      actions.setPlaying(false)
      return
    }
    const id = window.setInterval(() => {
      if (scanIdx >= scans.length - 1) {
        actions.setPlaying(false)
        window.clearInterval(id)
      } else {
        actions.setScanIdx(scanIdx + 1)
      }
    }, 650)
    return () => window.clearInterval(id)
  }, [playing, scanIdx, scans.length, reduceMotion, actions])

  // ----- zoom window over the daily domain (centred on the cursor) -----
  const fullN = Math.max(1, chart.domain.length)
  const cursorDayGlobal = Math.max(0, dayIndexOfScan(chart.domain, scan.scanAt))
  const win = useMemo(() => {
    const days = ZOOM_DAYS[zoom]
    if (!isFinite(days) || days >= fullN) return { start: 0, end: fullN - 1 }
    const half = Math.floor(days / 2)
    let start = cursorDayGlobal - half
    let end = cursorDayGlobal + half
    if (start < 0) {
      end -= start
      start = 0
    }
    if (end > fullN - 1) {
      start -= end - (fullN - 1)
      end = fullN - 1
    }
    return { start: Math.max(0, start), end: Math.min(fullN - 1, end) }
  }, [zoom, cursorDayGlobal, fullN])

  const n = Math.max(1, win.end - win.start + 1)
  const xAt = (localIdx: number) =>
    PL + (VBW - PL - prEff) * (n === 1 ? 0.5 : localIdx / (n - 1))
  const toLocal = (globalIdx: number) => globalIdx - win.start
  const inWin = (globalIdx: number) =>
    globalIdx >= win.start && globalIdx <= win.end

  const sliceSeries = (s: SeriesItem[]) => s.slice(win.start, win.end + 1)

  // ----- auto-scale Y to the visible data so the variation fills the chart -----
  const yRange = useMemo(() => {
    const vals: number[] = []
    const collect = (s: SeriesItem[]) =>
      s.slice(win.start, win.end + 1).forEach((it) => {
        if (it.value !== null) vals.push(it.value)
      })
    if (show.trend) collect(chart.trend)
    if (show.daily) collect(chart.daily)
    if (show.baseline) collect(chart.baseline)
    if (vals.length === 0) return { min: 0, max: 100 }
    let min = Math.min(...vals)
    let max = Math.max(...vals)
    const pad = Math.max(4, (max - min) * 0.18)
    min = Math.max(0, Math.floor((min - pad) / 5) * 5)
    max = Math.min(100, Math.ceil((max + pad) / 5) * 5)
    // keep a sensible floor span so tiny noise isn't visually exaggerated
    if (max - min < 20) {
      const mid = (min + max) / 2
      min = Math.max(0, Math.round(mid - 10))
      max = Math.min(100, Math.round(mid + 10))
    }
    return { min, max }
  }, [show, chart, win.start, win.end])

  const yAt = (v: number) =>
    PT + (VBH - PT - PB) * (1 - (v - yRange.min) / (yRange.max - yRange.min))
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) =>
    Math.round(yRange.min + (yRange.max - yRange.min) * f)
  )
  const cursorX = xAt(toLocal(cursorDayGlobal))

  // ----- lifestyle overlay: one signal aligned to the SAME daily domain, drawn
  // on an independent right axis auto-scaled to the visible window -----
  const overlay = useMemo(
    () =>
      overlayActive
        ? buildLifestyleOverlay(profile, overlayEff, chart.domain)
        : null,
    [overlayActive, overlayEff, profile, chart.domain]
  )
  const y2Range = useMemo(() => {
    if (!overlay) return { min: 0, max: 1 }
    const vals = overlay.series
      .slice(win.start, win.end + 1)
      .map((s) => s.value)
      .filter((v): v is number => v !== null)
    if (vals.length === 0) return { min: 0, max: 1 }
    let min = Math.min(...vals)
    let max = Math.max(...vals)
    if (min === max) {
      min -= 1
      max += 1
    }
    const pad = (max - min) * 0.12
    return { min: min - pad, max: max + pad }
  }, [overlay, win.start, win.end])
  const yAt2 = (v: number) =>
    PT + (VBH - PT - PB) * (1 - (v - y2Range.min) / (y2Range.max - y2Range.min))
  // Compact right-axis tick label: thousands collapse to "8k" so steps fit.
  const axisTick = (v: number) => {
    const r = Math.round(v)
    return Math.abs(r) >= 1000 ? `${Math.round(r / 100) / 10}k` : `${r}`
  }
  const rightTicks = overlay
    ? [y2Range.min, (y2Range.min + y2Range.max) / 2, y2Range.max]
    : []

  // ----- scrubber spans ALL scans (the zoom just magnifies the chart) -----
  const scanFrac = (i: number) => {
    const di = dayIndexOfScan(chart.domain, scans[i].scanAt)
    return fullN === 1 ? 0.5 : Math.max(0, di) / (fullN - 1)
  }
  const nearestScanToFrac = (f: number) => {
    let best = 0
    let bestD = Infinity
    scans.forEach((_, i) => {
      const d = Math.abs(scanFrac(i) - f)
      if (d < bestD) {
        bestD = d
        best = i
      }
    })
    return best
  }

  // ----- photo (scan-level; frame fixed regardless of overlay) -----
  const photo = vis.photos ? photoForScan(profile, scan.scanId) : undefined
  const series = seriesFor(profile, metric)
  const scanValue = series ? valueAt(series, scan.scanId) : undefined
  // Region dots for the CURRENT scan: prefer the lazy regional-trend (every
  // scan), fall back to the profile's regionalData (latest scan only) until the
  // trend loads. Photos-off never reaches here (dots overlay the photo).
  const trendItem = regionalDots?.get(scan.scanId)
  const regionDots = trendItem?.regionalData ?? scanValue?.regionalData ?? []
  // The image dimensions the landmark coords are normalized against — from the
  // SAME source as the coords (trend item, else the profile value) — so the
  // cover-crop projection lands the dots correctly.
  const landmarkImgW =
    trendItem?.landmarkImageWidth ?? scanValue?.landmarkImageWidth
  const landmarkImgH =
    trendItem?.landmarkImageHeight ?? scanValue?.landmarkImageHeight
  // Whether this metric has any regional breakdown to toggle (current scan or
  // anywhere in the loaded trend) — the toggle hides otherwise so it never shows
  // a dead control on a face-only metric or when region dots are gated off
  // (region dots need BOTH photos and metrics shared).
  const hasRegions =
    vis.regionDots && (regionDots.length > 0 || (regionalDots?.size ?? 0) > 0)
  // faceScore is the overall face-level value — it always drives the badge on
  // the photo, even when drilled into a region.
  const faceScore = displayScore(scanValue?.ema)
  // When drilled into a region, the legend readouts + the focused value tag
  // track THAT region's value at the cursor scan, not the face metric's.
  const regionCursor =
    selectedArea && regionalDots
      ? regionalDots
          .get(scan.scanId)
          ?.regionalData.find((d) => d.area === selectedArea)
      : undefined
  const cursorValue = selectedArea
    ? {
        ema: regionCursor?.ema,
        emaFast: regionCursor?.emaFast,
        baselineEma: regionCursor?.baselineEma,
      }
    : {
        ema: scanValue?.ema,
        emaFast: scanValue?.emaFast,
        baselineEma: scanValue?.baselineEma,
      }
  const cursorScore = displayScore(cursorValue.ema)

  // ----- drag / keyboard on the scrubber -----
  const trackRef = useRef<HTMLDivElement>(null)
  const dragging = useRef(false)
  const fracFromClientX = (clientX: number) => {
    const box = trackRef.current
    if (!box) return scanFrac(scanIdx)
    const r = box.getBoundingClientRect()
    return Math.max(0, Math.min(1, (clientX - r.left) / r.width))
  }
  const onDown = (e: React.PointerEvent) => {
    dragging.current = true
    trackRef.current?.setPointerCapture(e.pointerId)
    actions.setPlaying(false)
    actions.setScanIdx(nearestScanToFrac(fracFromClientX(e.clientX)))
  }
  const onMove = (e: React.PointerEvent) => {
    if (dragging.current)
      actions.setScanIdx(nearestScanToFrac(fracFromClientX(e.clientX)))
  }
  const onUp = () => {
    dragging.current = false
  }
  const onKey = (e: React.KeyboardEvent) => {
    const map: Record<string, number | undefined> = {
      ArrowLeft: scanIdx - 1,
      ArrowRight: scanIdx + 1,
      Home: 0,
      End: scans.length - 1,
    }
    const next = map[e.key]
    if (next !== undefined) {
      actions.setScanIdx(next)
      e.preventDefault()
    }
  }

  const legend: { key: LineKey; label: string; disabled: boolean }[] = [
    { key: 'trend', label: 'Trend', disabled: false },
    { key: 'daily', label: 'Daily', disabled: !chart.hasDaily },
    { key: 'baseline', label: 'Baseline', disabled: !chart.hasBaseline },
  ]
  const readout = (
    accessor: (v: typeof cursorValue) => number | null | undefined
  ) => displayScore(accessor(cursorValue))

  // One-section modes collapse the 2-col grid to a single column so the present
  // region fills the width instead of leaving the other column empty.
  const modeClass =
    vis.photos && vis.metrics
      ? ''
      : vis.photos
        ? ' player--photo-only'
        : ' player--chart-only'

  return (
    <section
      className={`player${expanded ? ' player--expanded' : ''}${modeClass}`}
    >
      {/* photo — fixed frame; region dots only where the scan carries them.
          Click (or the expand button) enlarges the photo to full card width. */}
      {vis.photos && (
        <div className='player__photo'>
          <div className='player__frame'>
            {photo?.imageUrl ? (
              <img
                className='player__img'
                src={photo.imageUrl}
                alt={`Scan from ${sDayJs(scan.scanAt).format('MMM D, YYYY')}`}
              />
            ) : (
              <AbstractFace className='player__img' />
            )}
            {/* Enlarge the photo to the bigger .player--expanded frame. Lives
                inside the photo block, so it follows the photo (shown in both
                photos-only and both modes) — never gate it on metrics. */}
            <button
              type='button'
              className='player__expand'
              aria-label={expanded ? 'Shrink photo' : 'Enlarge photo'}
              aria-pressed={expanded}
              onClick={(e) => {
                e.stopPropagation()
                setExpanded((v) => !v)
              }}
            >
              {expanded ? (
                <svg
                  width='16'
                  height='16'
                  viewBox='0 0 16 16'
                  aria-hidden='true'
                >
                  <path
                    d='M6 2v4H2M10 2v4h4M6 14v-4H2M10 14v-4h4'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='1.6'
                    strokeLinecap='round'
                  />
                </svg>
              ) : (
                <svg
                  width='16'
                  height='16'
                  viewBox='0 0 16 16'
                  aria-hidden='true'
                >
                  <path
                    d='M2 6V2h4M14 6V2h-4M2 10v4h4M14 10v4h-4'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='1.6'
                    strokeLinecap='round'
                  />
                </svg>
              )}
            </button>
            {vis.regionDots &&
              showRegions &&
              regionDots.flatMap((r) => {
                const score = displayScore(r.ema)
                if (score === null) return []
                const pts =
                  r.landmarks && r.landmarks.length > 0
                    ? r.landmarks
                    : r.landmark
                      ? [r.landmark]
                      : []
                return pts.map((pt, i) => {
                  const proj = projectCoverPoint(
                    pt.x,
                    pt.y,
                    landmarkImgW,
                    landmarkImgH,
                    FRAME_ASPECT
                  )
                  return (
                    <button
                      type='button'
                      key={`${r.area}-${i}`}
                      className={`player__dot${
                        selectedArea === r.area ? ' player__dot--on' : ''
                      }`}
                      style={
                        {
                          left: `${proj.left * 100}%`,
                          top: `${proj.top * 100}%`,
                          '--dot-color': color,
                        } as React.CSSProperties
                      }
                      disabled={!canDrill}
                      aria-pressed={selectedArea === r.area}
                      aria-label={
                        canDrill
                          ? `${labelForArea(r.area)} ${score}. Show this region's trend`
                          : `${labelForArea(r.area)} ${score}`
                      }
                      onClick={(e) => {
                        // don't let the tap bubble to the frame (which toggles expand)
                        e.stopPropagation()
                        setSelectedArea((a) => (a === r.area ? null : r.area))
                      }}
                    >
                      {score}
                    </button>
                  )
                })
              })}
            {/* the on-photo score badge reads a metric — only when metrics shared */}
            {vis.metrics && (
              <div
                className='player__badge'
                style={{ '--dot-color': color } as React.CSSProperties}
              >
                {faceScore ?? '—'}
              </div>
            )}
          </div>
          <div className='player__cap'>
            <span className='player__cap-date'>
              {sDayJs(scan.scanAt).format('MMM D, YYYY')}
            </span>
            <div className='player__cap-right'>
              {hasRegions && (
                <button
                  type='button'
                  className={`player__regions${
                    showRegions ? ' player__regions--on' : ''
                  }`}
                  aria-pressed={showRegions}
                  onClick={() => setShowRegions((v) => !v)}
                >
                  {showRegions ? 'Hide regions' : 'Show regions'}
                </button>
              )}
              <span className='player__cap-idx'>
                scan {scanIdx + 1} of {scans.length}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* chart + controls — shown when metrics are shared OR when a lifestyle
          overlay is available (metrics-off keeps the same chart, skin line empty) */}
      {(vis.metrics || lifeShared) && (
        <div className='player__chart'>
          {selectedArea && regionalItems.length > 0 && (
            <div className='player__region-head'>
              <button
                type='button'
                className='player__region-back'
                onClick={() => setSelectedArea(null)}
              >
                <svg
                  width='13'
                  height='13'
                  viewBox='0 0 16 16'
                  aria-hidden='true'
                >
                  <path
                    d='M10 3 5 8l5 5'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='1.8'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
                Overall face
              </button>
              <span
                className='player__region-name'
                style={{ '--dot-color': color } as React.CSSProperties}
              >
                {labelForArea(selectedArea)}
              </span>
            </div>
          )}
          {vis.metrics && (
            <div className='player__bar'>
              <div className='player__legend'>
                {legend.map((l) => (
                  <button
                    key={l.key}
                    type='button'
                    className={`leg leg--${l.key}${show[l.key] ? ' leg--on' : ''}`}
                    disabled={l.disabled}
                    aria-pressed={show[l.key]}
                    onClick={() => toggle(l.key)}
                    style={{ '--dot-color': color } as React.CSSProperties}
                  >
                    <span className='leg__mark' /> {l.label}
                    {l.key !== 'baseline' && (
                      <span className='leg__val'>
                        {readout((v) =>
                          l.key === 'trend' ? v?.ema : v?.emaFast
                        )}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* lifestyle overlay picker — one signal at a time on the right axis.
              "Off" only offered when metrics give a skin line to fall back to. */}
          {lifeShared && (
            <div
              className='player__overlay'
              role='group'
              aria-label='Lifestyle overlay'
            >
              <span className='player__overlay-label'>Lifestyle</span>
              {vis.metrics && (
                <button
                  type='button'
                  className={`ovl${overlayEff === 'off' ? ' ovl--on' : ''}`}
                  aria-pressed={overlayEff === 'off'}
                  onClick={() => setOverlaySig('off')}
                >
                  Off
                </button>
              )}
              {lifeSignals.map((s) => (
                <button
                  key={s.id}
                  type='button'
                  className={`ovl${overlayEff === s.id ? ' ovl--on' : ''}`}
                  aria-pressed={overlayEff === s.id}
                  onClick={() => setOverlaySig(s.id)}
                >
                  {s.label}
                </button>
              ))}
              {overlay && (
                <span className='player__overlay-val'>
                  {overlay.signal.format(overlay.latest)}
                </span>
              )}
            </div>
          )}

          <svg
            className='player__svg'
            viewBox={`0 0 ${VBW} ${VBH}`}
            preserveAspectRatio='none'
            role='img'
            aria-label='Metric trend'
          >
            {yTicks.map((v, i) => (
              <g key={i}>
                <line
                  className='grid'
                  x1={PL}
                  y1={yAt(v)}
                  x2={VBW - prEff}
                  y2={yAt(v)}
                />
                {vis.metrics && (
                  <text
                    className='axis'
                    x={PL - 6}
                    y={yAt(v) + 3}
                    textAnchor='end'
                  >
                    {v}
                  </text>
                )}
              </g>
            ))}
            {/* right axis — lifestyle units, only while an overlay is shown */}
            {overlay &&
              rightTicks.map((v, i) => (
                <text
                  key={`r${i}`}
                  className='axis axis--life'
                  x={VBW - prEff + 8}
                  y={yAt2(v) + 3}
                  textAnchor='start'
                >
                  {axisTick(v)}
                </text>
              ))}
            {profile.markers.map((m, i) => {
              const gi = dayIndexOfScan(chart.domain, m.date)
              const g = gi >= 0 ? gi : nearestDay(chart.domain, m.date)
              if (!inWin(g)) return null
              const x = xAt(toLocal(g))
              return (
                <line
                  key={i}
                  className='mk'
                  x1={x}
                  y1={PT}
                  x2={x}
                  y2={VBH - PB}
                />
              )
            })}
            <line
              className='cursor'
              x1={cursorX}
              y1={PT - 6}
              x2={cursorX}
              y2={VBH - PB}
            />
            {show.baseline && (
              <Path
                series={sliceSeries(chart.baseline)}
                xAt={xAt}
                yAt={yAt}
                className='ln ln--baseline'
                color={color}
              />
            )}
            {show.trend && (
              <Path
                series={sliceSeries(chart.trend)}
                xAt={xAt}
                yAt={yAt}
                className='ln ln--trend'
                color={color}
              />
            )}
            {show.daily && (
              <Path
                series={sliceSeries(chart.daily)}
                xAt={xAt}
                yAt={yAt}
                className='ln ln--daily'
                color={color}
              />
            )}
            {overlay && (
              <Path
                series={sliceSeries(overlay.series)}
                xAt={xAt}
                yAt={yAt2}
                className='ln ln--life'
                color='var(--life-line)'
              />
            )}
            {/* isolated overlay readings (both neighbours missing) as dots, so a
                sparse signal like sleep still shows instead of vanishing at gaps */}
            {overlay &&
              sliceSeries(overlay.series).map((it, i, arr) =>
                it.value !== null &&
                (i === 0 || arr[i - 1].value === null) &&
                (i === arr.length - 1 || arr[i + 1].value === null) ? (
                  <circle
                    key={`lo${i}`}
                    cx={xAt(i)}
                    cy={yAt2(it.value)}
                    r={2.6}
                    fill='var(--life-line)'
                  />
                ) : null
              )}
            {show.trend &&
              chart.trend.map((it, i) =>
                it.isScan && it.value !== null && inWin(i) ? (
                  <circle
                    key={i}
                    cx={xAt(toLocal(i))}
                    cy={yAt(it.value)}
                    r={it.scanId === scan.scanId ? 5 : 2.2}
                    fill={color}
                    style={{ opacity: it.scanId === scan.scanId ? 1 : 0.5 }}
                  />
                ) : null
              )}
            {/* focused value tag — tracks the charted line (face or region) */}
            {show.trend && cursorScore !== null && (
              <text
                className='vtag'
                x={cursorX + (cursorX > VBW / 2 ? -8 : 8)}
                y={yAt(cursorScore) - 8}
                textAnchor={cursorX > VBW / 2 ? 'end' : 'start'}
                fill={color}
              >
                {cursorScore}
              </text>
            )}
          </svg>
          {/* zoom sits UNDER the chart (a time-range selector) so the top of the
            chart stays uncluttered — one less chip cluster beside the photo */}
          <div className='player__zoom' role='group' aria-label='Zoom'>
            {(['1m', '3m', 'all'] as Zoom[]).map((z) => (
              <button
                key={z}
                type='button'
                className={`zoom${zoom === z ? ' zoom--on' : ''}`}
                aria-pressed={zoom === z}
                onClick={() => setZoom(z)}
              >
                {z === 'all' ? 'All' : z.toUpperCase()}
              </button>
            ))}
          </div>
          {/* scoring direction, right where the scores are read */}
          {vis.metrics && (
            <p className='score-note'>
              Every score is out of 100, where <strong>higher is better</strong>
              . A higher redness score, for example, means less redness.
            </p>
          )}
        </div>
      )}
      {/* scrubber — moves between the photo and the chart when expanded */}
      <div className='player__scrubarea'>
        <div className='player__scrub'>
          <button
            type='button'
            className='player__play'
            aria-label={playing ? 'Pause time-lapse' : 'Play time-lapse'}
            onClick={() => actions.togglePlay()}
          >
            {playing ? <PauseIcon /> : <PlayIcon />}
          </button>
          <div
            ref={trackRef}
            className='player__track'
            role='slider'
            tabIndex={0}
            aria-label='Scan date'
            aria-valuemin={1}
            aria-valuemax={scans.length}
            aria-valuenow={scanIdx + 1}
            onPointerDown={onDown}
            onPointerMove={onMove}
            onPointerUp={onUp}
            onPointerCancel={onUp}
            onKeyDown={onKey}
          >
            <span className='player__track-line' />
            <span
              className='player__track-fill'
              style={{ width: `${scanFrac(scanIdx) * 100}%` }}
            />
            {scans.map((s, i) => (
              <span
                key={s.scanId}
                className='player__tick'
                style={{ left: `${scanFrac(i) * 100}%` }}
                data-mk={profile.markers.some(
                  (m) =>
                    nearestDay(chart.domain, m.date) ===
                    dayIndexOfScan(chart.domain, s.scanAt)
                )}
              />
            ))}
            <span
              className='player__knob'
              style={{ left: `${scanFrac(scanIdx) * 100}%` }}
            />
          </div>
        </div>
        <div className='player__ends'>
          <span>
            {scans.length ? sDayJs(scans[0].scanAt).format('MMM D') : ''}
          </span>
          <span className='player__hint'>Drag, arrow keys, or press play</span>
          <span>
            {scans.length
              ? sDayJs(scans[scans.length - 1].scanAt).format('MMM D')
              : ''}
          </span>
        </div>
      </div>
    </section>
  )
}

/** A metric line with null-gap breaks. */
function Path({
  series,
  xAt,
  yAt,
  className,
  color,
}: {
  series: SeriesItem[]
  xAt: (i: number) => number
  yAt: (v: number) => number
  className: string
  color: string
}) {
  let d = ''
  let pen = false
  series.forEach((it, i) => {
    if (it.value === null) {
      pen = false
      return
    }
    d += `${pen ? 'L' : 'M'}${xAt(i)},${yAt(it.value)}`
    pen = true
  })
  if (!d) return null
  return <path className={className} d={d} stroke={color} />
}

/** Nearest domain index to a date (markers can fall between/around scans). */
function nearestDay(domain: string[], date: string): number {
  if (domain.length === 0) return 0
  const t = sDayJs(date).valueOf()
  let best = 0
  let bestD = Infinity
  domain.forEach((d, i) => {
    const diff = Math.abs(sDayJs(d).valueOf() - t)
    if (diff < bestD) {
      bestD = diff
      best = i
    }
  })
  return best
}

function PlayIcon() {
  return (
    <svg width='16' height='16' viewBox='0 0 16 16' aria-hidden='true'>
      <path d='M4 3l9 5-9 5V3z' fill='currentColor' />
    </svg>
  )
}
function PauseIcon() {
  return (
    <svg width='16' height='16' viewBox='0 0 16 16' aria-hidden='true'>
      <rect x='4' y='3' width='3' height='10' rx='1' fill='currentColor' />
      <rect x='9' y='3' width='3' height='10' rx='1' fill='currentColor' />
    </svg>
  )
}
